import { staleTimes, useQueries, useQuery } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import type { OptionIndex } from '../catalog/options.ts';
import { sellsFor } from '../companies.ts';
import { fetchStockLevels } from '../inventory/stock-levels.ts';
import { TYPING_DELAY_MS, useDebouncedValue } from '../useDebouncedValue.ts';
import { useKeysetPages } from '../useKeysetPages.ts';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import { saleableVariantsOf, type SaleableVariant } from './new-order-form.ts';
import type { ScannedCodes } from './useVariantScan.ts';

const PICKER_PAGE_SIZE = 20;

export type VariantOptionsInput = {
  readonly api: TypedClient;
  readonly company: string;
  readonly locationId: string;
  readonly index: OptionIndex;
  readonly text: string;
  readonly scanned: ScannedCodes | null;
  readonly opened: boolean;
};

const useSearchedProducts = (api: TypedClient, company: string, search: string, enabled: boolean) => {
  const { tenantId } = usePageContext();
  return useKeysetPages({
    queryKey: queryKeys.products(tenantId, { view: 'order-picker', search, company }),
    resource: 'catalog',
    enabled,
    fetchPage: (pageToken) =>
      api.get('/admin/products', {
        query: {
          status: 'all',
          for_company: company,
          page_size: PICKER_PAGE_SIZE,
          ...(search === '' ? {} : { search }),
          ...(pageToken === null ? {} : { page_token: pageToken }),
        },
      }),
  });
};

const useScannedProducts = (api: TypedClient, scanned: ScannedCodes | null) => {
  const { tenantId } = usePageContext();
  const products = useQueries({
    queries: (scanned?.productIds ?? []).map((id) => ({
      queryKey: queryKeys.product(tenantId, id),
      queryFn: () => api.get('/admin/products/{id}', { path: { id } }),
      staleTime: staleTimes.catalog,
    })),
  });
  return {
    items: products.flatMap((result) => (result.data === undefined ? [] : [result.data])),
    loading: products.some((result) => result.isPending),
  };
};

export const useVariantOptions = ({
  api,
  company,
  locationId,
  index,
  text,
  scanned,
  opened,
}: VariantOptionsInput) => {
  const { tenantId, locale } = usePageContext();
  const search = useDebouncedValue(text.trim(), TYPING_DELAY_MS);
  const pages = useSearchedProducts(api, company, search, opened && scanned === null);
  const scannedProducts = useScannedProducts(api, scanned);
  const products = scanned === null ? pages.items : scannedProducts.items;
  const sellable = products.filter(
    (product) => product.status !== 'archived' && sellsFor(product.subsidiaryId, company),
  );
  const variants = saleableVariantsOf(sellable, index, locale).filter(
    (variant) => scanned?.variantIds?.has(variant.variantId) ?? true,
  );
  const variantIds = variants.map((variant) => variant.variantId);
  const levels = useQuery({
    queryKey: queryKeys.stockLevels(tenantId, { variantIds }),
    queryFn: () => fetchStockLevels(api, variantIds),
    enabled: variantIds.length > 0,
    staleTime: staleTimes.inventory,
  });
  return {
    variants,
    loading: scanned === null ? pages.query.isPending || search !== text.trim() : scannedProducts.loading,
    hasMore: scanned === null && pages.hasMore,
    loadingMore: pages.loadingMore,
    loadMore: pages.loadMore,
    variantOf: (variantId: string): SaleableVariant | undefined =>
      variants.find((variant) => variant.variantId === variantId),
    availableOf: (variantId: string): number =>
      levels.data?.items.find((level) => level.variantId === variantId && level.locationId === locationId)
        ?.available ?? 0,
  };
};
